import * as XLSX from "xlsx";
import Papa from "papaparse";
// jspdf 4.x ships the constructor as a named export; its default is a namespace
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { excelSerial } from "@somasi/shared";

export type Row = Record<string, unknown>;

/** Hands the browser a file to save. Shared with the template writers. */
export function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * `2026-10-09-143207-somasi-companies.xlsx` — when the export was taken, first.
 *
 * Every export of a table lands in the same downloads folder under the same stem,
 * so without this the second one is `somasi-companies (1).xlsx` and which of the
 * two is the one taken after the filter changed is anybody's guess. Leading with
 * the date also files them in order in any folder sorted by name.
 *
 * Local time, because the person reading the name is the person who clicked it,
 * and to the second, because re-exporting the same table after a tweak is a thing
 * people do inside a minute. Only digits and hyphens: a colon is illegal in a
 * Windows filename and a space invites quoting bugs further down.
 */
export function exportStamp(when = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const date = [when.getFullYear(), pad(when.getMonth() + 1), pad(when.getDate())].join("-");
  const time = [pad(when.getHours()), pad(when.getMinutes()), pad(when.getSeconds())].join("");
  return `${date}-${time}`;
}

/** The caller's name, dated. Takes a stem or a name with its extension already on. */
function stamped(filename: string): string {
  return `${exportStamp()}-${filename}`;
}

export function exportCSV(filename: string, rows: Row[]) {
  const csv = Papa.unparse(rows);
  downloadBlob(
    stamped(filename.endsWith(".csv") ? filename : `${filename}.csv`),
    new Blob([csv], { type: "text/csv;charset=utf-8" }),
  );
}

/**
 * One table of a workbook as a .csv.
 *
 * Takes the sheet rather than its rows so the columns survive: a filter that
 * matched nothing would otherwise write a file of zero bytes, which reads as an
 * export that failed rather than as the empty answer it is. It also pins the column
 * order for a table whose headings are data — a column per fee schedule — where
 * deriving them from the first row means the order follows whichever row that was.
 */
export function exportSheetCSV(filename: string, sheet: Sheet) {
  const columns = sheet.columns ?? headersOf(sheet.rows);
  const csv =
    columns.length > 0
      ? Papa.unparse({
          fields: columns,
          data: sheet.rows.map((row) => columns.map((column) => row[column] ?? "")),
        })
      : // No rows, and a table whose headings come from its rows — so there are no
        // headings either. A sentence, because a file of zero bytes is the one
        // outcome indistinguishable from a download that went wrong.
        "Nothing to report.";
  downloadBlob(
    stamped(filename.endsWith(".csv") ? filename : `${filename}.csv`),
    new Blob([csv], { type: "text/csv;charset=utf-8" }),
  );
}

export function exportXLSX(filename: string, rows: Row[], sheetName = "Sheet1") {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
  XLSX.writeFile(wb, stamped(filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`));
}

/** One tab of a workbook, or one table of a report. */
export interface Sheet {
  name: string;
  rows: Row[];
  /**
   * The column order, and which columns appear at all.
   *
   * Given, the sheet is written with exactly these headers in exactly this order —
   * which is what a sheet whose rows are built per schedule needs, since a service
   * priced by nobody would otherwise contribute no keys and the schedule columns
   * would differ from file to file. Omitted, the keys of the rows are used.
   */
  columns?: string[];
  /**
   * Columns holding a `YYYY-MM-DD` string that must land as a real date cell.
   *
   * A date written as text is a date the reader has to be told about. Excel
   * left-aligns it, will not sort or filter it as a date, and an importer that
   * parses it is at the mercy of its own locale — which is the difference between
   * an accounting system reading 12 June and reading nothing at all.
   *
   * Only name a column here when something downstream needs the cell typed. A
   * reconciliation sheet is better off with the ISO text, which sorts correctly
   * as a string and says the same thing in every locale.
   */
  dateColumns?: string[];
  /** Columns holding a URL, written as a clickable hyperlink on the cell. */
  linkColumns?: string[];
}

/**
 * Excel's built-in date format, number 14, which is what the QuickBooks import
 * sheets carry.
 *
 * Spelled `m/d/yy` because that is the built-in's own definition, and writing it
 * that way makes the writer reference format 14 rather than define a custom one
 * that renders the same — so the file is indistinguishable from the sample
 * imports, with nothing extra in its style table. Excel shows format 14 as the
 * reader's short date, a four-digit year included; the two-digit year in the
 * name is not what lands on screen.
 */
const DATE_FORMAT = "m/d/yy";

/**
 * Several tables in one .xlsx, one per tab.
 *
 * A single sheet cannot hold an export of the catalogue *and* its pricing: the
 * catalogue is one row per service and the pricing is one row per service per
 * schedule per dated version, so flattening them together either repeats every
 * service field down the price rows or loses the prices. Tabs keep each table at
 * its own grain, in one file.
 *
 * An empty sheet is still written. "No service is priced outside the standard
 * list" is an answer, and a tab that vanished when it was true would read as an
 * export that had gone wrong.
 */
export function exportWorkbook(filename: string, sheets: Sheet[]) {
  const wb = XLSX.utils.book_new();
  const used = new Set<string>();

  for (const sheet of sheets) {
    const ws = sheetOf(sheet);
    // Excel refuses a workbook with two tabs of one name, and the 31-character
    // cap means two long names can collide after truncation.
    let name = sheet.name.slice(0, 31);
    for (let n = 2; used.has(name); n++) name = `${sheet.name.slice(0, 28)} ${n}`;
    used.add(name);
    XLSX.utils.book_append_sheet(wb, ws, name);
  }

  XLSX.writeFile(wb, stamped(filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`));
}

function sheetOf(sheet: Sheet): XLSX.WorkSheet {
  const columns = sheet.columns ?? headersOf(sheet.rows);
  const ws = XLSX.utils.json_to_sheet(sheet.rows, { header: columns });

  // A header row with no data rows under it: `json_to_sheet` writes nothing at
  // all for an empty array, so the headings are laid down directly. With nothing
  // to head either — a table whose columns come from its rows, and there are no
  // rows — a sentence goes in A1 instead, because a worksheet with no cells at
  // all carries no dimension and some readers call that a damaged file.
  if (sheet.rows.length === 0) {
    XLSX.utils.sheet_add_aoa(ws, columns.length > 0 ? [columns] : [["Nothing to report."]], {
      origin: "A1",
    });
  }

  // Retyped after the fact rather than written as `Date` objects: `json_to_sheet`
  // would shift each one by the exporter's UTC offset, and a column of dates is
  // the one thing in these files another system parses rather than reads.
  for (const column of sheet.dateColumns ?? []) {
    const index = columns.indexOf(column);
    if (index < 0) continue;
    for (let row = 0; row < sheet.rows.length; row++) {
      const address = XLSX.utils.encode_cell({ c: index, r: row + 1 });
      const cell = ws[address] as XLSX.CellObject | undefined;
      if (!cell || cell.t !== "s" || typeof cell.v !== "string") continue;
      const serial = excelSerial(cell.v);
      // A blank or an unparsed value is left exactly as it came: a due date
      // nobody set is absent, and guessing one would put a deadline in a file.
      if (serial == null) continue;
      ws[address] = { t: "n", v: serial, z: DATE_FORMAT };
    }
  }

  for (const column of sheet.linkColumns ?? []) {
    const index = columns.indexOf(column);
    if (index < 0) continue;
    for (let row = 0; row < sheet.rows.length; row++) {
      const cell = ws[XLSX.utils.encode_cell({ c: index, r: row + 1 })] as
        XLSX.CellObject | undefined;
      // Excel will not open a hyperlink longer than this; the URL stays as text.
      if (
        cell &&
        typeof cell.v === "string" &&
        /^https?:\/\//.test(cell.v) &&
        cell.v.length <= 2079
      ) {
        cell.l = { Target: cell.v };
      }
    }
  }

  ws["!cols"] = columns.map((column) => ({
    wch: Math.min(
      52,
      Math.max(
        10,
        column.length + 2,
        ...sheet.rows.slice(0, 200).map((row) => String(row[column] ?? "").length + 2),
      ),
    ),
  }));
  return ws;
}

/** Every key the rows use, in the order they first appear. */
function headersOf(rows: Row[]): string[] {
  const seen = new Set<string>();
  for (const row of rows) for (const key of Object.keys(row)) seen.add(key);
  return [...seen];
}

export interface PDFSection {
  /** Printed above the table. Omitted on a one-table report, which has a title. */
  heading?: string;
  /** A line under the heading saying what the table is, or what it leaves out. */
  note?: string;
  columns: { header: string; dataKey: string }[];
  rows: Row[];
  /** Starts this section on a fresh page. */
  pageBreak?: boolean;
}

export function exportPDF(opts: {
  filename: string;
  title: string;
  subtitle?: string;
  columns: { header: string; dataKey: string }[];
  rows: Row[];
  filters?: string;
}) {
  const { columns, rows, ...rest } = opts;
  exportPDFReport({ ...rest, sections: [{ columns, rows }] });
}

/**
 * A report of several tables, each with its own columns.
 *
 * Which is what pricing needs: the catalogue is one row per service, and what each
 * schedule charges is one row per service per schedule, so they are two tables with
 * nothing in common but the service. Printed as one table instead, either the
 * service fields repeat down every price row or the prices are left out — and a PDF
 * is the format most likely to be the thing that gets sent to a client, so it is
 * the one where that choice shows.
 */
export function exportPDFReport(opts: {
  filename: string;
  title: string;
  subtitle?: string;
  filters?: string;
  sections: PDFSection[];
}) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const now = new Date().toLocaleString();

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(20, 80, 90);
  doc.text("Somasi Corporate Services", 40, 40);

  doc.setFontSize(13);
  doc.setTextColor(30, 30, 30);
  doc.text(opts.title, 40, 62);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  if (opts.subtitle) doc.text(opts.subtitle, 40, 78);
  doc.text(`Generated: ${now}`, 40, 92);
  if (opts.filters) doc.text(`Filters: ${opts.filters}`, 40, 104);

  let cursor = 120;

  for (const [index, section] of opts.sections.entries()) {
    if (index > 0 && section.pageBreak) {
      doc.addPage();
      cursor = 48;
    }

    if (section.heading) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(30, 30, 30);
      doc.text(section.heading, 40, cursor);
      cursor += 14;

      if (section.note) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(110, 110, 110);
        doc.text(section.note, 40, cursor);
        cursor += 12;
      }
    }

    autoTable(doc, {
      startY: cursor,
      head: [section.columns.map((c) => c.header)],
      body:
        section.rows.length > 0
          ? section.rows.map((r) => section.columns.map((c) => formatCell(r[c.dataKey])))
          : [[{ content: "Nothing to report.", colSpan: section.columns.length }]],
      styles: { fontSize: 8, cellPadding: 4 },
      headStyles: { fillColor: [30, 95, 105], textColor: 255 },
      alternateRowStyles: { fillColor: [245, 249, 250] },
    });

    // Where the table ended, so the next section's heading sits under it rather
    // than on top of it. jspdf-autotable records this on the document.
    const ended = (doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY;
    cursor = (ended ?? cursor) + 28;
  }

  /*
   * The footers go on once every page exists.
   *
   * Written while each page was drawn, "of N" is the count *so far* — so page one
   * of a twelve-page report says "Page 1 of 1" and only the last page is right.
   * Nobody noticed while the reports were one table that mostly fitted; a report
   * of several sections is pages either way.
   */
  const pageCount = doc.getNumberOfPages();
  const pageSize = doc.internal.pageSize;
  for (let page = 1; page <= pageCount; page++) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(120, 120, 120);
    doc.text(`Page ${page} of ${pageCount}`, pageSize.getWidth() - 80, pageSize.getHeight() - 20);
  }

  doc.save(stamped(opts.filename.endsWith(".pdf") ? opts.filename : `${opts.filename}.pdf`));
}

function formatCell(v: unknown): string {
  if (v == null) return "";
  if (v instanceof Date) return v.toLocaleDateString();
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}
